import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { config } from "@core/config/config";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { Application } from "@domain/application/application";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { AuthModal } from "@ui/components/auth/authModal";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { BigArrowIcon } from "@ui/components/icons";
import { useModalTools } from "@ui/modals/modalContext";
import { mobile } from "@ui/styles/utils";
import { OmatModalIno } from "./omatModalInfo";

interface ApplyButtonProps extends React.HTMLAttributes<HTMLDivElement> {
	label?: string;
	application?: Application | null;
	isBig?: boolean;
}
export const ApplyButton: React.FC<ApplyButtonProps> = ({ label, application, isBig, ...htmlProps }) => {
	const currentProfile = useCurrentProfile();
	const { createModal } = useModalTools();
	const { trackingService } = useServices();
	const history = useHistory();
	const handleApply = (): void => {
		trackingService.track("Apply_now_omat");
		if (!currentProfile) {
			createModal((close) => <AuthModal close={close} />);
			return;
		}

		if (application) {
			history.push(
				application.status === "PENDING_VALIDATION"
					? Path.ApplicationSheet(currentProfile)
					: Path.ApplicationEditor(application.id, "OMAT")
			);
			return;
		}

		if (!application) {
			createModal((close) => <OmatModalIno close={close} />);
			return;
		}
	};
	return isBig ? (
		<Container {...htmlProps} onClick={() => handleApply()} disabled={!config.features.program}>
			<StyledLabel>
				{I18n.t(
					!currentProfile || !application ? "application.button.start_title" : "application.button.terminate_title"
				)}
			</StyledLabel>
			<StyledArrowIcons />
		</Container>
	) : (
		<StyledButtonContainer onClick={() => handleApply()} disabled={!config.features.program}>
			{I18n.t(
				!currentProfile || !application ? "application.button.start_title" : "application.button.terminate_title"
			)}
		</StyledButtonContainer>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["isBig", "disabled"].includes(prop),
})<{ isBig?: boolean; disabled: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	position: relative;
	cursor: pointer;
	${({ theme }) => theme.textStyles.text};
	background: ${({ theme }) => theme.colors.secondary};
	border-radius: 20px;
	width: 455.98px;
	height: 90px;
	color: ${({ theme }) => theme.colors.white};

	@media ${mobile} {
		height: 60px;
	}

	${({ disabled }) =>
		disabled &&
		css`
			pointer-events: none;
			background-color: ${({ theme }) => theme.colors.lightgray};
			color: ${({ theme }) => theme.colors.lightergray};
			box-shadow: none;
			cursor: none;
		`}
`;

const StyledButtonContainer = styled(PrimaryButton)`
	padding: 18px;
`;

const StyledLabel = styled.div`
	font-weight: 500;
	font-size: 30px;
	margin-left: 40px;
	@media ${mobile} {
		font-size: 24px;
	}
`;

const StyledArrowIcons = styled(BigArrowIcon)`
	position: absolute;
	right: 40px;
`;
